import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BadgeCheck, Download, Loader2, Pencil, Plus, Search, Sparkles, Trash2, Upload, X,
} from 'lucide-react'
import { nsApi } from '../services/api'
import { useToast } from '../context/ToastContext'

// Org-wide lookup table: the name a supplier prints on its documents (e.g.
// "ORDATRONICS PTY LTD") → the NetSuite customer/vendor it really is. Rows are
// learned automatically when a reviewer confirms a match during review, and
// can be maintained here by hand or in bulk via CSV when names change.

const TABS = [
  { key: 'customer', label: 'Customers / Resellers' },
  { key: 'vendor', label: 'Vendors' },
]

function recordName(r) {
  return r?.name || r?.companyname || r?.entityid || r?.altname || String(r?.id ?? '')
}

function formatWhen(v) {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString()
}

function errMsg(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback
}

// Debounced live search against the same NetSuite customer/vendor search
// endpoints the request review views use.
function RecordPicker({ entityType, value, onChange, inputId }) {
  const [query, setQuery] = useState(value?.ns_name || '')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const q = query.trim()
    if (!open || q.length < 2) { setResults([]); return }
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      const call = entityType === 'vendor' ? nsApi.searchVendors(q) : nsApi.searchCustomers(q)
      call
        .then(res => {
          if (!cancelled) setResults(res.data?.customers || res.data?.vendors || [])
        })
        .catch(() => { if (!cancelled) setResults([]) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query, open, entityType])

  return (
    <div className="relative">
      <input
        id={inputId}
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange(null) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={`Search NetSuite ${entityType === 'vendor' ? 'vendors' : 'customers'}…`}
        className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        autoComplete="off"
      />
      {value && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Selected: {value.ns_name} (#{value.ns_internal_id})</p>
      )}
      {open && query.trim().length >= 2 && (
        <ul role="listbox" className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {loading && (
            <li className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</li>
          )}
          {!loading && results.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-500">No matches</li>
          )}
          {!loading && results.map(r => (
            <li key={r.id} role="option" aria-selected={value?.ns_internal_id === String(r.id)}>
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  const picked = { ns_internal_id: String(r.id), ns_name: recordName(r) }
                  onChange(picked); setQuery(picked.ns_name); setOpen(false)
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {recordName(r)} <span className="text-xs text-gray-400">#{r.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function MappingForm({ entityType, initial, onCancel, onSaved }) {
  const toast = useToast()
  const [sourceName, setSourceName] = useState(initial?.source_name || '')
  const [record, setRecord] = useState(
    initial ? { ns_internal_id: String(initial.ns_internal_id), ns_name: initial.ns_name } : null
  )
  const [saving, setSaving] = useState(false)
  const idBase = initial ? `edit-${initial.mapping_id}` : 'new-mapping'

  const submit = async (e) => {
    e.preventDefault()
    if (!sourceName.trim() || !record) return
    setSaving(true)
    try {
      const res = await nsApi.saveNameMapping({
        entity_type: entityType,
        source_name: sourceName.trim(),
        ns_internal_id: record.ns_internal_id,
        ns_name: record.ns_name,
        // An edit updates THIS row (renaming it if the source name changed);
        // without the id the backend would upsert a second row beside it.
        ...(initial?.mapping_id != null ? { mapping_id: initial.mapping_id } : {}),
      })
      toast.success('Mapping saved')
      onSaved(res.data?.mapping)
    } catch (err) {
      toast.error(errMsg(err, 'Could not save mapping'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-[1fr_1fr_auto] items-start p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
      <div>
        <label htmlFor={`${idBase}-source`} className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Name on documents</label>
        <input
          id={`${idBase}-source`}
          type="text"
          value={sourceName}
          onChange={e => setSourceName(e.target.value)}
          placeholder="e.g. ORDATRONICS PTY LTD"
          className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>
      <div>
        <label htmlFor={`${idBase}-record`} className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">NetSuite record</label>
        <RecordPicker entityType={entityType} value={record} onChange={setRecord} inputId={`${idBase}-record`} />
      </div>
      <div className="flex gap-2 md:pt-5">
        <button
          type="submit"
          disabled={saving || !sourceName.trim() || !record}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-navy-800 text-white disabled:opacity-50 flex items-center gap-1"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
          Cancel
        </button>
      </div>
    </form>
  )
}

export default function NSNameMappings() {
  const toast = useToast()
  const [entityType, setEntityType] = useState('customer')
  const [search, setSearch] = useState('')
  const [mappings, setMappings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const fileRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await nsApi.listNameMappings({ entity_type: entityType, q: search.trim() || undefined })
      setMappings(res.data?.mappings || [])
    } catch (err) {
      setError(errMsg(err, 'Could not load name mappings'))
      setMappings([])
    } finally {
      setLoading(false)
    }
  }, [entityType, search])

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [load, search])

  const switchTab = (key) => {
    setEntityType(key); setAdding(false); setEditingId(null); setImportResult(null)
  }

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete the mapping for "${m.source_name}"?`)) return
    try {
      await nsApi.deleteNameMapping(m.mapping_id)
      setMappings(prev => prev.filter(x => x.mapping_id !== m.mapping_id))
      toast.success('Mapping deleted')
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete mapping'))
    }
  }

  const handleImport = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setImporting(true); setImportResult(null)
    try {
      const res = await nsApi.importNameMappings(file, entityType)
      setImportResult(res.data || {})
      load()
    } catch (err) {
      toast.error(errMsg(err, 'Import failed'))
    } finally {
      setImporting(false)
    }
  }

  const handleExport = async () => {
    try {
      const res = await nsApi.exportNameMappings(entityType)
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `netsuite-${entityType}-name-mappings.csv`
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // responseType 'blob' turns a JSON error body into a Blob — read it back.
      const data = err?.response?.data
      if (data && typeof data.text === 'function') {
        try { err.response.data = JSON.parse(await data.text()) } catch { /* not JSON */ }
      }
      toast.error(errMsg(err, 'Export failed'))
    }
  }

  const onSaved = () => { setAdding(false); setEditingId(null); load() }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Name mappings</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          How names printed on supplier documents map to NetSuite records. Shared across all templates in your organisation.
        </p>
      </div>

      <div role="tablist" aria-label="Mapping type" className="inline-flex gap-1 rounded-lg border border-gray-200 dark:border-gray-800 p-1">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={entityType === t.key}
            onClick={() => switchTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              entityType === t.key
                ? 'bg-navy-800 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <label htmlFor="ns-name-mapping-search" className="sr-only">Search mappings</label>
          <input
            id="ns-name-mapping-search"
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search names…"
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <button
          type="button"
          onClick={() => { setAdding(true); setEditingId(null) }}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-navy-800 text-white flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> Add mapping
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="hidden" aria-label="Import CSV file" data-testid="ns-mapping-import-input" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-1 disabled:opacity-50"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
        </button>
        <button
          type="button"
          onClick={handleExport}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-1"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">
        CSV columns: source_name, ns_internal_id, ns_name (optional), entity_type (optional).
      </p>

      {importResult && (
        <div role="status" className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 p-3 text-sm text-blue-900 dark:text-blue-200">
          <div className="flex items-start justify-between gap-2">
            <p>
              Import finished: {importResult.inserted ?? 0} added, {importResult.updated ?? 0} updated
              {(importResult.errors?.length || 0) > 0 && `, ${importResult.errors.length} rows skipped`}.
            </p>
            <button type="button" onClick={() => setImportResult(null)} aria-label="Dismiss import summary"><X className="w-4 h-4" /></button>
          </div>
          {(importResult.errors?.length || 0) > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-red-700 dark:text-red-300">
              {importResult.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}

      {adding && (
        <MappingForm entityType={entityType} onCancel={() => setAdding(false)} onSaved={onSaved} />
      )}

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : mappings.length === 0 && !error ? (
        <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
          <p className="font-medium text-gray-700 dark:text-gray-300">{search ? 'No mappings match your search.' : 'No name mappings yet.'}</p>
          <p className="mt-1">Names confirmed during review are saved here automatically. You can also add them by hand or import a CSV.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2 pr-3">Name on documents</th>
                <th className="py-2 pr-3">NetSuite record</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Confirmed</th>
                <th className="py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => editingId === m.mapping_id ? (
                <tr key={m.mapping_id}>
                  <td colSpan={5} className="py-2">
                    <MappingForm entityType={entityType} initial={m} onCancel={() => setEditingId(null)} onSaved={onSaved} />
                  </td>
                </tr>
              ) : (
                <tr key={m.mapping_id} className="border-b border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                  <td className="py-2 pr-3 font-medium">{m.source_name}</td>
                  <td className="py-2 pr-3">
                    {m.ns_name || '—'} <span className="text-xs text-gray-400">#{m.ns_internal_id}</span>
                  </td>
                  <td className="py-2 pr-3">
                    {m.is_verified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                        <Sparkles className="w-3 h-3" /> Learned
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                    {m.confirmed_by || '—'}
                    {m.confirmed_at && <div>{formatWhen(m.confirmed_at)}</div>}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button type="button" onClick={() => { setEditingId(m.mapping_id); setAdding(false) }} aria-label={`Edit mapping for ${m.source_name}`} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => handleDelete(m)} aria-label={`Delete mapping for ${m.source_name}`} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
